/**
 * DeviationHeatmap — a CSS-grid heatmap of open deviations broken down
 * by department (rows) × criticality (columns). Each cell's saturation
 * scales with count; critical-non-zero cells get a red border so they
 * jump out to the reviewer.
 */

export interface DeviationHeatmapRow {
  dept: string;
  critical: number;
  major: number;
  minor: number;
}

export interface DeviationHeatmapProps {
  rows: DeviationHeatmapRow[];
}

function cellShade(value: number, max: number, hueDeg: number): string {
  if (value === 0) return 'transparent';
  // Alpha scales 0.18 → 0.80 with severity. color-mix against the card
  // surface keeps the cell legible in both light and dark (the bg-surface
  // token redefines itself per theme).
  const alpha = 0.18 + Math.min(0.62, (value / Math.max(1, max)) * 0.62);
  return `color-mix(in srgb, hsl(${hueDeg}deg 70% 50%) ${Math.round(alpha * 100)}%, var(--color-bg-surface-elevated, #fff))`;
}

export function DeviationHeatmap({ rows }: DeviationHeatmapProps) {
  const maxC = Math.max(1, ...rows.map((r) => r.critical));
  const maxM = Math.max(1, ...rows.map((r) => r.major));
  const maxL = Math.max(1, ...rows.map((r) => r.minor));
  // hue tokens: red for critical (0), amber for major (40), green for minor (140)
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'separate', borderSpacing: 4, fontSize: 13 }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'start', color: 'var(--color-text-tertiary, #6B7989)', fontWeight: 500, padding: '4px 8px' }}>Department</th>
            <th style={{ padding: '4px 8px', color: 'var(--color-status-error, #B91C1C)' }}>Critical</th>
            <th style={{ padding: '4px 8px', color: 'var(--color-status-warning, #B45309)' }}>Major</th>
            <th style={{ padding: '4px 8px', color: 'var(--color-text-secondary, #4B5563)' }}>Minor</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.dept}>
              <td style={{ padding: '6px 8px', color: 'var(--color-text-primary, #111827)', whiteSpace: 'nowrap' }}>{r.dept}</td>
              <td style={{
                padding: '6px 12px', textAlign: 'center', minWidth: 56,
                background: cellShade(r.critical, maxC, 0),
                border: r.critical > 0 ? '1px solid var(--color-status-error, #B91C1C)' : '1px solid transparent',
                borderRadius: 6, fontWeight: r.critical > 0 ? 600 : 400, color: r.critical > 0 ? 'var(--color-status-error, #B91C1C)' : 'inherit',
              }}>{r.critical}</td>
              <td style={{
                padding: '6px 12px', textAlign: 'center', minWidth: 56,
                background: cellShade(r.major, maxM, 38),
                borderRadius: 6, fontWeight: r.major > 0 ? 600 : 400,
              }}>{r.major}</td>
              <td style={{
                padding: '6px 12px', textAlign: 'center', minWidth: 56,
                background: cellShade(r.minor, maxL, 142),
                borderRadius: 6,
              }}>{r.minor}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
